import { type JSX, useEffect, useState } from "react";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table, { DrawerSize } from "@via/components/table/table.tsx";
import useUrlParams from "@via/hooks/useURLParams.tsx";
import type { FoundryModel } from "@via/pages/cortex/foundry-models/foundry-models.api.tsx";
import {
  useDeleteFoundryModel,
  useFoundryModelsList,
} from "@via/pages/cortex/foundry-models/foundry-models.api.tsx";
import { BUTTONS, EMPTY_TABLE_MESSAGE, ERROR } from "@via/utils/constants.tsx";
import { classNames, formatDateString } from "@via/utils/helpers.tsx";
import FoundryModelsFilter from "@via/pages/cortex/foundry-models/foundry-models-filter.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { ActionButton } from "@via/components/action-button/action-button.tsx";
import Drawer from "@via/components/drawer.tsx";
import CreateFoundryModelForm from "@via/pages/cortex/foundry-models/create-foundry-model-form.tsx";

const defaultParams = {
  sort: [{ id: "createdAt", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

const FOUNDRY_MODEL_TABLE_OPTIONS = [{ name: "Delete", href: "#" }];

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const statusConfig: Record<
    string,
    { label: string; bgColor: string; textColor: string }
  > = {
    Training: {
      label: "Training",
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
    },
    Deployed: {
      label: "Deployed",
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
    },
    Testing: {
      label: "Testing",
      bgColor: "bg-purple-100",
      textColor: "text-purple-800",
    },
    Production: {
      label: "Production",
      bgColor: "bg-green-100",
      textColor: "text-green-800",
    },
    Archived: {
      label: "Archived",
      bgColor: "bg-gray-100",
      textColor: "text-gray-800",
    },
  };

  const config = statusConfig[status] || {
    label: status,
    bgColor: "bg-gray-100",
    textColor: "text-gray-800",
  };

  return (
    <span
      className={classNames(
        config.bgColor,
        config.textColor,
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap"
      )}>
      {config.label}
    </span>
  );
};

// Task Type badge component
const TaskTypeBadge = ({ taskType }: { taskType: string }) => {
  return (
    <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800 whitespace-nowrap">
      {taskType}
    </span>
  );
};

// Deployment Target component
const DeploymentTargetCell = ({
  deploymentTarget,
}: {
  deploymentTarget?: {
    computeType: string;
    region: string;
    endpointUrl?: string;
    containerVersion?: string;
  };
}) => {
  if (!deploymentTarget) {
    return <span className="text-gray-400">Not deployed</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium">
          {deploymentTarget.computeType}
        </span>
        <span className="text-xs text-gray-500">•</span>
        <span className="text-xs text-gray-600">{deploymentTarget.region}</span>
      </div>
      {deploymentTarget.containerVersion && (
        <span className="text-xs text-gray-500">
          Container: {deploymentTarget.containerVersion}
        </span>
      )}
    </div>
  );
};

const FoundryModels = (): JSX.Element => {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [open, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(initialParams.filters);
  const [tableData, setTableData] = useState<FoundryModel[]>([]);
  const [modelToBeDeleted, setModelToBeDeleted] = useState<FoundryModel | null>(
    null
  );

  const {
    data: foundryModelsList,
    isError: isFoundryModelsListError,
    isLoading: isFoundryModelsListLoading,
  } = useFoundryModelsList(page, pageSize, sort, columnFilters);

  const { mutate: deleteModel } = useDeleteFoundryModel();

  useEffect(() => {
    if (foundryModelsList?.data) {
      setTableData(foundryModelsList.data);
    }
  }, [foundryModelsList]);

  const handleContextMenuAction = (name: string, model: FoundryModel): void => {
    switch (name) {
      case "Delete":
        setModelToBeDeleted(model);
        break;
    }
  };

  useEffect(() => {
    setUrlParams({ sort, filters: columnFilters, page, pageSize });
  }, [sort, columnFilters, page, pageSize]);

  const columnHelper = createColumnHelper<FoundryModel>();
  const columns = [
    columnHelper.accessor("name", {
      cell: ({ row }) => {
        return (
          <span className="truncate font-medium">{row.original.name}</span>
        );
      },
      header: () => <span>Model Name</span>,
      size: 200,
    }),
    columnHelper.accessor("version", {
      cell: info => (
        <span className="block truncate text-sm">{info.getValue()}</span>
      ),
      header: () => <span>Version</span>,
      size: 120,
      enableSorting: false,
    }),
    columnHelper.accessor("status", {
      cell: info => {
        return <StatusBadge status={info.getValue()} />;
      },
      header: () => <span>Status / Stage</span>,
      size: 130,
      enableSorting: false,
    }),
    columnHelper.accessor("taskType", {
      cell: info => {
        return <TaskTypeBadge taskType={info.getValue()} />;
      },
      header: () => <span>Type / Task</span>,
      size: 150,
      enableSorting: false,
    }),
    columnHelper.accessor("owner", {
      cell: info => {
        return <span className="text-sm mr-5">{info.getValue()}</span>;
      },
      header: () => <span>Owner / Project</span>,
      size: 150,
      enableSorting: false,
    }),
    columnHelper.accessor("deploymentTarget", {
      cell: ({ row }) => {
        return (
          <DeploymentTargetCell
            deploymentTarget={row.original.deploymentTarget}
          />
        );
      },
      header: () => <span>Deployment Target / Endpoint</span>,
      size: 220,
      enableSorting: false,
    }),
    columnHelper.accessor("updatedAt", {
      cell: info => {
        const value = info.getValue();
        return (
          <span className="text-sm">
            {formatDateString(value.toString(), "LL")}
          </span>
        );
      },
      header: () => <span>Updated</span>,
      size: 140,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return (
          <span className="text-sm">
            {formatDateString(value.toString(), "LL")}
          </span>
        );
      },
      header: () => <span>Created</span>,
      size: 140,
    }),
    columnHelper.accessor("id", {
      cell: ({ row }) => {
        const contextMenuOptions = FOUNDRY_MODEL_TABLE_OPTIONS;

        return (
          <TableContextMenu
            options={contextMenuOptions}
            onSelect={option => {
              handleContextMenuAction(option.name, row.original);
            }}
          />
        );
      },
      header: () => "Options",
      size: 70,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<FoundryModel>>;

  return (
    <main className="py-4">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Foundry Models</h2>
        <ActionButton
          button={BUTTONS.REGISTER_MODEL}
          primary={true}
          onClick={() => {
            setDrawerOpen(true);
          }}
        />
      </div>
      <Drawer
        id="cortex-create-foundry-model"
        open={open}
        setOpen={setDrawerOpen}
        size={DrawerSize.Medium}>
        <CreateFoundryModelForm setOpen={setDrawerOpen} />
      </Drawer>

      <DeleteModal
        item={modelToBeDeleted}
        setItem={setModelToBeDeleted}
        deleteFunction={async () => {
          if (modelToBeDeleted) {
            deleteModel(modelToBeDeleted.id);
          }
        }}
        isLoading={false}
        title="Delete Foundry Model"
      />

      {isFoundryModelsListError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isFoundryModelsListLoading}
          data={tableData}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={foundryModelsList?.total}
          emptyMessage={EMPTY_TABLE_MESSAGE.DEFAULT}
          applyColumnSize={true}
          additionalFilters={
            <FoundryModelsFilter
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setPage={setPage}
              setPageSize={setPageSize}
            />
          }
        />
      )}
    </main>
  );
};

export default FoundryModels;
